import React, { useContext, useMemo } from 'react';
import moment, { Moment } from 'moment';

import Day from './Day';
import { WeekElement } from './shared.styles';
import { DateRangeContext } from 'context/dateRangeContext';

interface IWeekProps {
  isFirst: boolean;
  week: { days: number; start: number };
  month: number;
  year: number;
  fromDate: Moment | undefined;
  toDate: Moment | undefined;
  totalDay: number;
  minDate?: Moment;
  maxDate?: Moment;
}

const Week: React.FC<IWeekProps> = ({
  isFirst,
  week,
  month,
  year,
  fromDate,
  toDate,
  totalDay,
  maxDate,
  minDate,
}) => {
  const dateRangeContext = useContext(DateRangeContext);
  const { datePeriod, withPeriod, onSelectDate, onHoverDate, hoverDate } =
    dateRangeContext;

  const { minSelectebleDay, maxSelectebleDay } = useMemo(() => {
    let minDay: any;
    let minSelectebleDay: any;
    let maxSelectebleDay: any;

    if (withPeriod) {
      // TODO remove this condition when we will use Date Picket with Daily option in all components
      minDay = toDate
        ? datePeriod === 'week'
          ? moment(toDate).subtract(52, 'weeks')
          : moment(toDate).subtract(29, 'days')
        : (fromDate &&
            datePeriod === 'week' &&
            moment(fromDate) < moment().subtract(52, 'weeks')) ||
          (fromDate &&
            datePeriod === 'day' &&
            moment(fromDate) < moment().subtract(29, 'days'))
        ? moment(fromDate)
        : undefined;

      minSelectebleDay = minDay
        ? moment(minDate).isAfter(minDay, 'day')
          ? minDate
          : minDay
        : minDate;

      maxSelectebleDay =
        fromDate &&
        datePeriod === 'week' &&
        moment(fromDate).add(52, 'weeks') < (maxDate ? maxDate : moment())
          ? moment(fromDate).add(52, 'weeks')
          : fromDate &&
            datePeriod === 'day' &&
            moment(fromDate).add(29, 'days') < (maxDate ? maxDate : moment())
          ? moment(fromDate).add(29, 'days')
          : maxDate
          ? maxDate
          : moment();
    } else {
      minDay = toDate
        ? moment(toDate).subtract(52, 'weeks')
        : fromDate && moment(fromDate) < moment().subtract(52, 'weeks')
        ? moment(fromDate)
        : moment().subtract(52, 'weeks');

      minSelectebleDay = moment(minDate).isAfter(minDay, 'day')
        ? minDate
        : minDay;

      maxSelectebleDay =
        fromDate &&
        moment(fromDate).add(52, 'weeks') < (maxDate ? maxDate : moment())
          ? moment(fromDate).add(52, 'weeks')
          : maxDate
          ? maxDate
          : moment();
    }

    return { minSelectebleDay, maxSelectebleDay };
  }, [withPeriod, datePeriod, fromDate, toDate]);

  function generateDay(): JSX.Element[] {
    return [...Array(week.days).keys()].map((index) => {
      const dateIndex = index + week.start;
      const dateValue = moment.utc(
        `${year}-${month + 1}-${dateIndex}`,
        'YYYY-MM-DD'
      );
      const disabled =
        (minSelectebleDay && dateValue.isBefore(minSelectebleDay, 'day')) ||
        (maxSelectebleDay && dateValue.isAfter(maxSelectebleDay, 'day'));

      const selected =
        (fromDate && dateValue.isSame(fromDate, 'day')) ||
        (toDate && dateValue.isSame(toDate, 'day'));

      let hovered = false;
      const highlight = dateValue.isSame(minDate, 'day');

      if (fromDate && !fromDate.isSame(toDate, 'day')) {
        if (
          toDate &&
          !fromDate.isAfter(dateValue, 'day') &&
          !toDate.isBefore(dateValue, 'day')
        ) {
          hovered = true;
        }
        if (
          !toDate &&
          !dateValue.isBefore(fromDate, 'day') &&
          !(hoverDate && hoverDate.isBefore(dateValue, 'day')) &&
          fromDate.isBefore(hoverDate, 'day')
        ) {
          hovered = true;
        }
      }

      let isEndDate = false;
      if (
        dateValue.isSame(toDate, 'day') ||
        (!toDate && dateValue.isSame(hoverDate, 'day'))
      ) {
        isEndDate = true;
      }

      return (
        <Day
          key={index}
          dateIndex={dateIndex}
          dateValue={dateValue}
          onSelectDate={onSelectDate}
          onHoverDate={onHoverDate}
          selected={selected}
          hovered={hovered}
          highlight={highlight}
          disabled={disabled}
          isEndDay={isEndDate}
          totalDay={totalDay}
        />
      );
    });
  }

  return <WeekElement isFirst={isFirst}>{generateDay()}</WeekElement>;
};

export default Week;
